import { motion } from "framer-motion";
import { Languages, Sparkles } from "lucide-react";
import type { RoadmapLanguage, RoadmapView } from "./types";

type RoadmapHeroProps = {
  activeView: RoadmapView;
  hasRoadmap: boolean;
  language: RoadmapLanguage;
  onViewChange: (view: RoadmapView) => void;
  onLanguageChange: (language: RoadmapLanguage) => void;
};

const copy = {
  en: {
    eyebrow: "Your next career breakthrough starts here",
    title: "A clearer next credential starts here.",
    description:
      "Answer a short questionnaire and get a personalized certification path based on your stage, track, and goals.",
    roadmap: "My roadmap",
    quiz: "Find my path",
    discover: "Explore Professional Certifications",
    language: "Change language",
  },
  ar: {
    eyebrow: "خطوتك المهنية القادمة تبدأ من هنا",
    title: "اختر شهادتك القادمة بوضوح وثقة.",
    description:
      "أجب عن أسئلة قصيرة لتحصل على مسار شهادات مهني يناسب مرحلتك وتخصصك وأهدافك.",
    roadmap: "خارطتي المهنية",
    quiz: "اكتشف مساري",
    discover: "استكشف الشهادات المهنية",
    language: "تغيير اللغة",
  },
} as const;

const RoadmapHero = ({
  activeView,
  hasRoadmap,
  language,
  onViewChange,
  onLanguageChange,
}: RoadmapHeroProps) => {
  const text = copy[language];

  return (
  <section className="relative bg-primary pb-20 pt-16 text-white sm:pt-24">
    <div className="pointer-events-none absolute right-[-10%] top-[-35%] h-[420px] w-[420px] rounded-full border-[70px] border-secondary/20" />
    <div className="container relative pt-8 sm:pt-6">
      <div className="mb-8 flex justify-end">
        <div
          role="group"
          aria-label={text.language}
          className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/10 p-1 backdrop-blur-sm"
        >
          <Languages size={16} className="mx-2 text-white/70" />
          {(["en", "ar"] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={language === option}
              onClick={() => onLanguageChange(option)}
              className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${
                language === option
                  ? "bg-white text-primary shadow-sm"
                  : "text-white/70 hover:text-white"
              }`}
            >
              {option.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl"
      >
        <div className="mb-5 flex items-center gap-2 sm:text-sm text-xs font-bold uppercase tracking-[0.16em] text-[#ffb59e]">
          <Sparkles className="sm:size-4 min-w-3 min-h-3" /> {text.eyebrow}
        </div>
        <h1 className="max-w-2xl text-3xl font-bold leading-[1.08] sm:text-6xl">
          {text.title}
        </h1>
        <p className="mt-5 max-w-xl text-sm leading-7 text-white/75 sm:text-base">
          {text.description}
        </p>
      </motion.div>

      <div className="mt-10 flex w-full max-w-xl gap-2 rounded-2xl border border-white/15 bg-white/10 p-1.5 backdrop-blur-sm">
        {(
          [
            ["quiz", hasRoadmap ? text.roadmap : text.quiz],
            ["discover", text.discover],
          ] as const
        ).map(([view, label]) => (
          <button
            key={view}
            onClick={() => onViewChange(view)}
            className={`flex-1 rounded-xl px-3 py-3 text-xs font-bold transition-all sm:text-sm ${
              activeView === view
                ? "bg-white text-primary shadow-lg"
                : "text-white/70 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  </section>
  );
};

export default RoadmapHero;
